import {
  Content,
  Portal as RadixPortal,
  Trigger as RadixTrigger,
  Root,
} from "@radix-ui/react-popover";
import Clock from "app/components/Icons/Clock";
import Input from "app/components/Input";
import { parseEffortInput } from "app/containers/Features/helper";
import clsx from "clsx";
import React, { FC, useState } from "react";
import { TimeTrackerStatusIcon } from "./TimeTrackerStatusIcon";
import { TimeInputFieldProps } from "./types";

const TimeInputField: FC<TimeInputFieldProps> = ({
  containerClassName,
  inputProps,
  onClickTimerIcon,
  enablePreview,
  previewValue,
  onClickPreview,
  timerRunning,
  hideIcon = false,
}) => {
  const [menuVisibility, setMenuVisibility] = useState(false);

  const inputValue = (inputProps?.value as string) ?? "";
  const parsedEffort = parseEffortInput(inputValue);
  const isPopoverOpen = menuVisibility && parsedEffort.isValid;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setMenuVisibility(next.trim().length > 0);
    inputProps?.onChange?.(e);
  };

  return (
    <div className={clsx("w-full", containerClassName)}>
      <Root
        open={isPopoverOpen}
        onOpenChange={(visible) => setMenuVisibility(visible)}
      >
        {enablePreview ? (
          <div
            className="rounded px-2 py-2.5 flex items-center justify-between cursor-pointer border border-white"
            onClick={onClickPreview}
          >
            <span className="text-sm text-black">{previewValue}</span>
            <Clock color="#79808A" size={24} />
          </div>
        ) : (
          <RadixTrigger asChild>
            <div>
              <Input
                {...inputProps}
                id={inputProps?.id || "time-input"}
                onChange={handleChange}
                endIcon={
                  hideIcon ? null : (
                    <TimeTrackerStatusIcon
                      isActive={timerRunning}
                      size={24}
                      timerIconProps={{
                        onClick: (e) => {
                          e.stopPropagation();
                          if (onClickTimerIcon) onClickTimerIcon(e);
                        },
                      }}
                    />
                  )
                }
                inputClassName={clsx(
                  "!h-auto !rounded !px-2 !py-0 !justify-between",
                  inputProps?.disabled && "cursor-not-allowed opacity-60",
                  inputProps?.inputClassName,
                )}
                className={clsx(
                  inputProps?.className,
                  "!min-w-0 !flex-1 !text-sm !font-normal !leading-none !text-black !py-[15px]",
                )}
              />
            </div>
          </RadixTrigger>
        )}

        <RadixPortal>
          <Content
            align="start"
            sideOffset={2}
            onOpenAutoFocus={(e) => e.preventDefault()}
            className="min-w-[var(--radix-popover-trigger-width)] max-w-full flex flex-col bg-white rounded border border-gray-100 shadow-[2px_2px_6px_0px_rgba(237,239,242,0.70)] z-50 py-2.5"
          >
            <p className="text-sm text-secondary px-3 py-2 hover:bg-gray-400 cursor-pointer">
              {parsedEffort.formatted}
            </p>
          </Content>
        </RadixPortal>
      </Root>
    </div>
  );
};

TimeInputField.displayName = "TimeInputField";

export default TimeInputField;
